import axios, { AxiosError } from 'axios';
import { auth } from '../firebase';

interface ApiError {
  message?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

async function getAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('User not logged in');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

export type BusinessStateGoal = {
  label: string;
  metric: string | null;
  target: number | null;
};

export type BusinessStateRisk = {
  label: string;
  type?: string;
  severity?: string;
};

export type BusinessStateSnapshot = {
  userBusinessId: number;
  phase: string;
  counts: {
    products: number;
    orders: number;
    clients: number;
  };
  revenue: number;
  currentMetrics: Record<string, number>;
  currentGoals: BusinessStateGoal[];
  currentOpportunities: unknown[];
  currentRisks: BusinessStateRisk[];
  lastEventAt: string | null;
  projectionVersion: number;
  projectedAt: string;
  updatedAt: string;
};

export type BusinessMetric = {
  id: string;
  name: string;
  value: number;
  window: string;
  projectionVersion: number;
  updatedAt: string;
};

export type BusinessMetricsResponse = {
  userBusinessId: number;
  metrics: BusinessMetric[];
};

export type BusinessEventRecord = {
  id: string;
  userBusinessId: number;
  type: string;
  occurredAt: string;
  source: string;
  version: number;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type BusinessEventsResponse = {
  userBusinessId: number;
  eventSchemaVersion: number;
  total: number;
  limit: number;
  offset: number;
  events: BusinessEventRecord[];
};

export type BusinessObservation = {
  id: string;
  userBusinessId: number;
  type: string;
  summary: string;
  severity: 'info' | 'warning' | 'critical' | string;
  evidence: {
    metrics: unknown[];
    stateFields: string[];
    eventIds: string[];
    values: Record<string, unknown>;
  };
  observedAt: string;
  expiresAt: string | null;
  projectionVersion: number;
  createdAt: string;
  updatedAt: string;
};

export type BusinessObservationsResponse = {
  userBusinessId: number;
  projectionVersion: number;
  observations: BusinessObservation[];
};

export type BeliefObservationRef = {
  id: string;
  type: string;
  summary: string;
};

export type BeliefSupportingEvidence = {
  observations: BeliefObservationRef[];
  reasoning: string;
};

export type BeliefCounterEvidenceItem = {
  summary: string;
  observationId?: string;
};

export type BusinessBelief = {
  id: string;
  userBusinessId: number;
  topic: string;
  description: string;
  confidence: number;
  supportingEvidence: BeliefSupportingEvidence;
  counterEvidence: BeliefCounterEvidenceItem[] | null;
  status: string;
  projectionVersion: number;
  createdAt: string;
  updatedAt: string;
};

export type BusinessBeliefsResponse = {
  userBusinessId: number;
  projectionVersion: number;
  beliefs: BusinessBelief[];
};

export type BusinessDecision = {
  id: string;
  userBusinessId: number;
  conversationId: string | null;
  messageId: string | null;
  type: string;
  status: string;
  summary: string;
  diff: {
    description?: string;
    priority?: string;
    confidence?: number;
    [key: string]: unknown;
  } | null;
  executionPlan: Record<string, unknown> | null;
  resolvedAt: string | null;
  expectedOutcome: string | null;
  actualOutcome: string | null;
  evaluatedAt: string | null;
  evaluationStatus?: string | null;
  learningResult?: Record<string, unknown> | null;
  correlationId: string | null;
  supportingBeliefIds: string[];
  projectionVersion: number;
  createdAt: string;
  updatedAt: string;
};

export type BusinessDecisionsResponse = {
  userBusinessId: number;
  projectionVersion: number;
  decisions: BusinessDecision[];
};

export type BusinessBrainContextMeta = {
  payloadBytes: number;
  estimatedTokens: number;
  tokenBudget: number;
  truncated: boolean;
  options?: Record<string, unknown>;
};

export type BusinessBrainContext = {
  version: number;
  userBusinessId: number;
  assembledAt: string;
  businessSummary: Record<string, unknown>;
  businessState: BusinessStateSnapshot | null;
  goals: unknown[];
  metrics: BusinessMetric[];
  recentEvents: BusinessEventRecord[];
  activeObservations: BusinessObservation[];
  activeBeliefs: BusinessBelief[];
  activeDecisions: BusinessDecision[];
  risks: unknown[];
  opportunities: unknown[];
  recentActions: Record<string, unknown>[];
  conversationContext: Record<string, unknown>[];
  recommendationHistory: Record<string, unknown>[];
  sectionCounts: Record<string, number>;
  meta?: BusinessBrainContextMeta;
};

export type LearningBeliefEvolution = {
  beliefId: string;
  topic: string;
  description: string;
  previousConfidence: number;
  newConfidence: number;
  previousStatus: string;
  newStatus: string;
  reason: string;
  proposalId: string;
};

export type LearningDecisionRecord = BusinessDecision & {
  evaluationStatus?: string | null;
  learningResult?: {
    beliefAdjustments?: LearningBeliefEvolution[];
    linkedEventTypes?: string[];
  } | null;
};

export type LearningHistoryResponse = {
  userBusinessId: number;
  decisions: LearningDecisionRecord[];
  beliefEvolutions: LearningBeliefEvolution[];
};

export type LearningStatistics = {
  userBusinessId: number;
  decisionsGenerated: number;
  decisionsApproved: number;
  decisionsEvaluated: number;
  successfulDecisions: number;
  failedDecisions: number;
  partialDecisions: number;
  unknownDecisions: number;
  successRate: number;
  averageConfidence: number;
  refutedBeliefs: number;
  beliefsUpdated: number;
  averageEvaluationTimeDays: number;
};

export const get_business_state = async (
  userBusinessId: number
): Promise<BusinessStateSnapshot> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `business-state/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load business state'
    );
  }
};

export const get_business_metrics = async (
  userBusinessId: number
): Promise<BusinessMetricsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `metrics/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load metrics'
    );
  }
};

export const get_business_events = async (
  userBusinessId: number,
  options?: { limit?: number; offset?: number; type?: string }
): Promise<BusinessEventsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params: Record<string, string | number> = {};
    if (options?.limit != null) params.limit = options.limit;
    if (options?.offset != null) params.offset = options.offset;
    if (options?.type) params.type = options.type;

    const response = await backendAPI.get(
      `business-events/business/${userBusinessId}`,
      { headers, params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load business events'
    );
  }
};

export type ProjectionReplayResult = {
  userBusinessId: number;
  eventCount: number;
  replayedAt: string;
  metrics: {
    before: Record<string, number>;
    after: Record<string, number>;
    changed: boolean;
  };
  state: {
    beforePhase: string | null;
    afterPhase: string | null;
    projectedAt: string | null;
    lastEventAt: string | null;
  };
};

export const replay_business_projections = async (
  userBusinessId: number
): Promise<ProjectionReplayResult> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.post(
      `business-events/business/${userBusinessId}/replay`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to replay projections'
    );
  }
};

export const get_business_observations = async (
  userBusinessId: number
): Promise<BusinessObservationsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `observations/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load observations'
    );
  }
};

export const get_business_beliefs = async (
  userBusinessId: number
): Promise<BusinessBeliefsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `beliefs/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load beliefs'
    );
  }
};

export const generate_business_beliefs = async (
  userBusinessId: number
): Promise<BusinessBeliefsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.post(
      `beliefs/business/${userBusinessId}/generate`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to generate beliefs'
    );
  }
};

export const get_business_decisions = async (
  userBusinessId: number,
  options?: { status?: string; limit?: number }
): Promise<BusinessDecisionsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params: Record<string, string | number> = {};
    if (options?.status) params.status = options.status;
    if (options?.limit != null) params.limit = options.limit;

    const response = await backendAPI.get(
      `decisions/business/${userBusinessId}`,
      { headers, params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load decisions'
    );
  }
};

export const generate_business_decisions = async (
  userBusinessId: number
): Promise<BusinessDecisionsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.post(
      `decisions/business/${userBusinessId}/generate`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to generate decisions'
    );
  }
};

export const get_business_context = async (
  userBusinessId: number,
  options?: { includeConversation?: boolean }
): Promise<BusinessBrainContext> => {
  try {
    const headers = await getAuthHeaders();
    const params: Record<string, string> = {};
    if (options?.includeConversation) {
      params.includeConversation = 'true';
    }

    const response = await backendAPI.get(
      `context/business/${userBusinessId}`,
      { headers, params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load business context'
    );
  }
};

export const get_learning_history = async (
  userBusinessId: number
): Promise<LearningHistoryResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `learning/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load learning history'
    );
  }
};

export const get_learning_statistics = async (
  userBusinessId: number
): Promise<LearningStatistics> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `learning/statistics/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        'Failed to load learning statistics'
    );
  }
};
